/**
 * The unified dashboard's address (#1337): the tab and every filter live in
 * the URL, so a link, «Назад» and a reload land on the same screen. Shared by
 * the portal's `/dashboard` and the Bitrix24 tab, so the parameter names are
 * the shell's own — none of them is read by the embedded route's other tabs
 * (`tab`, `period`, `projectIds`, `from`/`to` belong to them).
 *
 * The window is half-open, `[start, end)`, as the quality slice's (E1): the
 * previous window the routes derive is then exactly as long as this one.
 */
import { EMPLOYEE_ISSUES_KEY_MAX, PROJECT_IDS_MAX } from '@aica/api-types';
import { dateTimeFormatter } from '@/lib/utils';
import { getPeriodDateRange } from '@/hooks/usePeriodNavigation';
import { dayParts } from '@/components/analytics/objections/objections-search';

export const UNIFIED_SECTIONS = [
  'summary',
  'flow',
  'sales',
  'quality',
  'team',
  'voc',
  'mine',
] as const;
export type UnifiedSection = (typeof UNIFIED_SECTIONS)[number];

/**
 * The two views of the shell (#1338 PR C): the business tabs for whoever
 * reads the organization's numbers, and the employee's own — one tab, «Моё»,
 * every number about their own conversations. Neither view offers the other's
 * tabs.
 */
export const BUSINESS_SECTIONS = [
  'summary',
  'flow',
  'sales',
  'quality',
  'team',
  'voc',
] as const satisfies readonly UnifiedSection[];
export const PERSONAL_SECTIONS = ['mine'] as const satisfies readonly UnifiedSection[];

export type UnifiedView = 'business' | 'personal';

/** «последние 7 полных дней» (default) · 30 days · the last full quarter · custom. */
export const UNIFIED_RANGES = ['7d', '30d', 'quarter', 'custom'] as const;
export type UnifiedRange = (typeof UNIFIED_RANGES)[number];

export const UNIFIED_CHANNELS = ['call', 'chat'] as const;
export type UnifiedChannel = (typeof UNIFIED_CHANNELS)[number];

/**
 * Every field optional: an absent one is its default (the summary tab, the
 * last 7 full days, compared with the previous period, every project, both
 * channels, demo as the organization's demo mode says).
 */
export interface UnifiedDashboardSearch {
  section?: UnifiedSection;
  range?: UnifiedRange;
  /** `custom` only: the first and the last day, `YYYY-MM-DD`, both included. */
  rangeFrom?: string;
  rangeTo?: string;
  /** `none` — no comparison; absent — with the previous period of the same length. */
  compare?: 'none';
  projects?: string[];
  channel?: UnifiedChannel;
  demo?: 'yes' | 'no';
  /**
   * «Команда»: the employee whose dips are open (#767) — the roster row's key
   * (`DashboardTeamEmployee.key`), so a link opens the same person.
   */
  employee?: string;
  /**
   * The address named a custom range that cannot be asked for, and the window
   * fell back to the default: the filter panel says so. Written by the parser
   * and kept in the address until the period is chosen again.
   */
  rangeRejected?: true;
}

/**
 * The widest window the domain routes accept — a year and a day, in
 * milliseconds, as the server measures it (`MAX_WINDOW_MS` in
 * `apps/edge-api/src/routes/dashboard-quality.ts`: `length > 366 * DAY_MS` is a
 * 400). Counted in milliseconds of the local half-open window, not in calendar
 * days: a range across two clock changes back is an hour longer than its days.
 */
export const UNIFIED_MAX_RANGE_MS = 366 * 86_400_000;

function pick<T extends string>(values: readonly T[], value: unknown): T | undefined {
  return values.find((candidate) => candidate === value);
}

/** Why a custom range cannot be asked for; `null` — it can. */
export type CustomRangeProblem = 'invalid' | 'reversed' | 'tooLong';

/**
 * Can the days `from`..`to` (both included) be asked for: two real dates, in
 * order, whose half-open window `[from, to + 1 day)` is no longer than the
 * server accepts.
 */
export function customRangeProblem(
  from: unknown,
  to: unknown
): CustomRangeProblem | null {
  const a = dayParts(from);
  const b = dayParts(to);
  if (!a || !b) return 'invalid';
  const length =
    new Date(b[0], b[1] - 1, b[2] + 1).getTime() -
    new Date(a[0], a[1] - 1, a[2]).getTime();
  if (length <= 0) return 'reversed';
  return length > UNIFIED_MAX_RANGE_MS ? 'tooLong' : null;
}

/**
 * The shell's parameters out of a raw search object; anything else is dropped,
 * so a hand-edited address never becomes a permanent 400. A custom range
 * that cannot be asked for falls back to the default range — and says so
 * (`rangeRejected`), instead of silently showing another period.
 */
export function parseUnifiedDashboardSearch(
  search: Record<string, unknown>
): UnifiedDashboardSearch {
  const section = pick(UNIFIED_SECTIONS, search.section);
  let range = pick(UNIFIED_RANGES, search.range);
  const custom =
    range === 'custom'
      ? customRangeProblem(search.rangeFrom, search.rangeTo) === null
      : false;
  const rejected =
    (range === 'custom' && !custom) ||
    (range === undefined &&
      (search.rangeRejected === true || search.rangeRejected === 'true'));
  if (range === 'custom' && !custom) range = undefined;
  // A string from a link ("a,b"); an array once the router re-serialized it.
  const raw = search.projects;
  const items = Array.isArray(raw)
    ? raw.filter((id): id is string => typeof id === 'string')
    : typeof raw === 'string'
      ? raw.split(',')
      : [];
  const projects = [...new Set(items.map((id) => id.trim()))]
    .filter((id) => id.length > 0 && id.length <= 100)
    .slice(0, PROJECT_IDS_MAX);
  const channel = pick(UNIFIED_CHANNELS, search.channel);
  const demo = pick(['yes', 'no'] as const, search.demo);
  const employee =
    typeof search.employee === 'string' &&
    search.employee.length > 0 &&
    search.employee.length <= EMPLOYEE_ISSUES_KEY_MAX
      ? search.employee
      : undefined;
  return {
    ...(section ? { section } : {}),
    ...(range ? { range } : {}),
    ...(custom
      ? { rangeFrom: search.rangeFrom as string, rangeTo: search.rangeTo as string }
      : {}),
    ...(search.compare === 'none' ? { compare: 'none' as const } : {}),
    ...(projects.length > 0 ? { projects } : {}),
    ...(channel ? { channel } : {}),
    ...(demo ? { demo } : {}),
    ...(employee ? { employee } : {}),
    ...(rejected ? { rangeRejected: true as const } : {}),
  };
}

export interface UnifiedWindow {
  range: UnifiedRange;
  /** Inclusive start, local midnight. */
  start: Date;
  /** Exclusive end, local midnight: the window is `[start, end)`. */
  end: Date;
}

function localMidnight(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

/**
 * The window the filters name, at `now`. Rolling ranges end at today's local
 * midnight — full days only, today is not one yet. «Квартал» is the last full
 * calendar quarter (the prototype's «III квартал» on 2 October), cut by the
 * same rule as the old dashboards' quarter (`getPeriodDateRange`).
 */
export function unifiedWindow(search: UnifiedDashboardSearch, now: Date): UnifiedWindow {
  const today = localMidnight(now);
  const range = search.range ?? '7d';
  let start: Date;
  let end: Date;
  // One condition for the window and for the name it goes by: a custom range
  // missing an end is the default window, and says «7d», not «custom».
  const custom = range === 'custom' && !!search.rangeFrom && !!search.rangeTo;
  if (custom) {
    const from = dayParts(search.rangeFrom)!;
    const to = dayParts(search.rangeTo)!;
    start = new Date(from[0], from[1] - 1, from[2]);
    end = new Date(to[0], to[1] - 1, to[2] + 1);
  } else if (range === 'quarter') {
    const thisQuarter = getPeriodDateRange('quarter', today, 1).start;
    const last = getPeriodDateRange('quarter', addDays(thisQuarter, -1), 1);
    start = last.start;
    end = thisQuarter;
  } else {
    end = today;
    start = addDays(today, range === '30d' ? -30 : -7);
  }
  return {
    range: range === 'custom' && !custom ? '7d' : range,
    start,
    end,
  };
}

/**
 * «7 окт. – 13 окт.» — the days a window spans, for a card's period label. The
 * year is drawn when the window starts and ends in different years:
 * «31 дек. 2026 – 6 янв. 2027». Without it a window a full year long would
 * read «7 окт. – 6 окт.». `locale` is an argument so that a change
 * of language is a change of the label.
 */
export function periodDatesLabel(window: UnifiedWindow, locale: string): string {
  const last = new Date(window.end.getTime() - 1);
  const format = dateTimeFormatter(
    {
      day: 'numeric',
      month: 'short',
      ...(window.start.getFullYear() !== last.getFullYear() ? { year: 'numeric' } : {}),
    },
    locale
  );
  return `${format.format(window.start)} – ${format.format(last)}`;
}
